import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Canvas from './Canvas';
import { useEditorStore } from '../stores/editorStore';
import { ArrowLeft, Edit } from 'lucide-react';

const Player = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const { loadProject, project, isLoading } = useEditorStore();
    const [hotspotContent, setHotspotContent] = useState<string | null>(null);
    const [tooltip, setTooltip] = useState<{ content: string; x: number; y: number } | null>(null);

    useEffect(() => {
        if (projectId) {
            loadProject(projectId);
        }
    }, [projectId, loadProject]);

    if (isLoading || !project) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50">
                <div className="text-gray-500">Caricamento...</div>
            </div>
        );
    }

    const handleHotspotClick = (id: string) => {
        const hotspot = project.elements.find(el => el.id === id);
        if (hotspot && hotspot.type === 'hotspot') {
            if (!hotspot.trigger || hotspot.trigger === 'click') {
                setHotspotContent(hotspot.content || 'Nessun contenuto');
            }
        }
    };

    const handleHotspotMouseEnter = (id: string, e: any) => {
        const hotspot = project.elements.find(el => el.id === id);
        if (hotspot && hotspot.type === 'hotspot' && hotspot.trigger === 'hover') {
            const clientX = e.evt.clientX;
            const clientY = e.evt.clientY;
            setTooltip({
                content: hotspot.content || '',
                x: clientX,
                y: clientY
            });
        }
    };

    const handleHotspotMouseLeave = (id: string) => {
        const hotspot = project.elements.find(el => el.id === id);
        if (hotspot && hotspot.type === 'hotspot' && hotspot.trigger === 'hover') {
            setTooltip(null);
        }
    };

    return (
        <div className="h-screen w-screen bg-black relative flex flex-col overflow-hidden">
            {/* Top Bar for navigation back (optional, mostly for testing user flow) */}
            <div className="absolute top-4 left-4 z-10 flex gap-2">
                <button
                    onClick={() => navigate('/')} // Back to dashboard
                    className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-colors"
                    title="Torna alla Dashboard"
                >
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <button
                    onClick={() => navigate(`/editor/${projectId}`)}
                    className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-colors flex items-center gap-2 px-4"
                    title="Torna all'Editor"
                >
                    <Edit className="w-5 h-5" />
                    <span className="font-medium">Modifica</span>
                </button>
            </div>

            <div className="flex-1 relative">
                <Canvas
                    imageUrl={project.backgroundImage}
                    width={project.width}
                    height={project.height}
                    readOnly={true}
                    onHotspotClick={handleHotspotClick}
                    onHotspotMouseEnter={handleHotspotMouseEnter}
                    onHotspotMouseLeave={handleHotspotMouseLeave}
                />
            </div>

            {/* Tooltip for Hover */}
            {tooltip && (
                <div
                    className="absolute z-40 bg-white/90 backdrop-blur text-gray-800 p-3 rounded-lg shadow-xl max-w-xs pointer-events-none border border-gray-200"
                    style={{
                        top: tooltip.y + 15,
                        left: tooltip.x + 15,
                        animation: 'fadeIn 0.2s ease-out'
                    }}
                >
                    <div className="text-sm font-medium">{tooltip.content}</div>
                </div>
            )}

            {/* Hotspot Content Overlay */}
            {hotspotContent !== null && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setHotspotContent(null)}>
                    <div
                        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 relative animate-in fade-in zoom-in duration-200"
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setHotspotContent(null)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="prose max-w-none">
                            <h3 className="text-xl font-bold mb-4">Contenuto Hotspot</h3>
                            <div className="whitespace-pre-wrap text-gray-700">
                                {hotspotContent}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Player;
